import gql from 'graphql-tag'

type LandingPageQueryParameters = {
  first?: number
  categories?: string[]
  tags?: string[]
  categorySlug?: string
}

export const landingPageQuery = ({
  first = 4,
  categories = [],
  tags = [],
  categorySlug = ''
}: LandingPageQueryParameters) => {
  return gql`
    {
      textSamples: posts(
        first: ${first}
        where: {
          taxQuery: {
            relation: AND,
            taxArray: [
              { terms: ["content-category-text"], taxonomy: CATEGORY, operator: IN, field: SLUG }
              ${categories
                .filter(item => item)
                .map(
                  item =>
                    `{ terms: ["${item}"], taxonomy: CATEGORY, operator: IN, field: SLUG }`
                )},
              ${tags.map(
                item =>
                  `{ terms: ["${item}"], taxonomy: TAG, operator: IN, field: SLUG }`
              )},
            ]
          }
        }
      ) {
        nodes {
          ...post
        }
      }
      designSamples: posts(
        first: ${first}
        where: {
          taxQuery: {
            relation: AND,
            taxArray: [
              { terms: ["content-category-design"], taxonomy: CATEGORY, operator: IN, field: SLUG }
              ${categories
                .filter(item => item)
                .map(
                  item =>
                    `{ terms: ["${item}"], taxonomy: CATEGORY, operator: IN, field: SLUG }`
                )},
              ${tags.map(
                item =>
                  `{ terms: ["${item}"], taxonomy: TAG, operator: IN, field: SLUG }`
              )},
            ]
          }
        }
      ) {
        nodes {
          ...post
        }
      }
      languageSamples: posts(
        first: ${first}
        where: {
          taxQuery: {
            relation: AND,
            taxArray: [
              { terms: ["content-category-language-translation"], taxonomy: CATEGORY, operator: IN, field: SLUG }
              ${categories
                .filter(item => item)
                .map(
                  item =>
                    `{ terms: ["${item}"], taxonomy: CATEGORY, operator: IN, field: SLUG }`
                )},
              ${tags.map(
                item =>
                  `{ terms: ["${item}"], taxonomy: TAG, operator: IN, field: SLUG }`
              )},
            ]
          }
        }
      ) {
        nodes {
          ...post
        }
      }

      # industries category list
      industries: categories(where: { slug: "industry" }) {
        nodes {
          children(first: 50) {
            edges {
              node {
                id
                name
                slug
              }
            }
          }
        }
      }

      # fetch industry category details if present
      categoryDetails: category(id: "${categorySlug}", idType: SLUG) {
        id
        name
        description
        slug
      }
    }

    fragment post on Post {
      id
      title
      slug
      categories {
        edges {
          isPrimary
          node {
            id
            name
            slug
          }
        }
      }
      sampleTextPostMetadata {
        wordCount
      }
      sampleDesignPostMetadata {

        purpose
        image {
          sourceUrl
          mediaDetails {
            height
            width
          }
        }
      }
    }
  `
}

export const contentCategoryPageQuery = (categorySlug: WpContentTypeSlug) => {
  return gql`
    {
      contentTypes: categories(where: { slug: "${categorySlug}" }) {
        nodes {
          name
          children(first: 50) {
            nodes {
              ...categoryNode
            }
          }
        }
      }
      industries: categories(where: { slug: "industry" }) {
        nodes {
          name
          children(first: 50) {
            nodes {
              ...categoryNode
            }
          }
        }
      }
      creatorLevel: categories(where: { slug: "creator-level" }) {
        nodes {
          name
          children {
            nodes {
              ...categoryNode
            }
          }
        }
      }
    }
    fragment categoryNode on Category {
      id
      name
      slug
    }
  `
}

type SampleListQueryParameters = {
  categories?: string[]
  tags?: string[]
  limit?: number | null
  after?: string
  categorySlug?: string
}

export const sampleListQuery = ({
  categories = [],
  tags = [],
  limit = 8,
  after = '',
  categorySlug = ''
}: SampleListQueryParameters) => {
  return gql`
    {
      posts(
        first: ${limit}
        after: "${after}"
        where: {
          taxQuery: {
            relation: AND,
            taxArray: [
              ${categories
                .filter(item => item)
                .map(item => {
                  return `{ terms: ["${item}"], taxonomy: CATEGORY, operator: IN, field: SLUG }`
                })},
              ${tags.map(
                item =>
                  `{ terms: ["${item}"], taxonomy: TAG, operator: IN, field: SLUG }`
              )}
            ]
          }
        }
      ) {
        nodes {
          ...post
        }
        pageInfo {
          endCursor
          startCursor
          hasNextPage
        }
      }

      # fetch category details if categorySlug is present
      categoryDetails: category(id: "${categorySlug}", idType: SLUG) {
        id
        name
        description
        slug
      }
    }

    fragment post on Post {
      id
      title
      slug
      date
      categories {
        edges {
          isPrimary
          node {
            id
            name
            slug
          }
        }
      }
      sampleTextPostMetadata {
        wordCount
      }
      sampleDesignPostMetadata {
        purpose
        image {
          sourceUrl
          mediaDetails {
            height
            width
          }
        }
      }
    }
  `
}

export const sampleDetailsPage = (slug: string): string => {
  return ` {
    post: post(idType: URI, id: "${slug}") {
      id
      title
      excerpt
      content
      slug
      date
      modified
      categories {
        edges {
          isPrimary
          node {
            id
            name
            slug
            parentDatabaseId
          }
        }
      }
      author {
        node {
          name
        }
      }
      featuredImage {
        node {
          sourceUrl(size: POST_THUMBNAIL)
        }
      }
      sampleTextPostMetadata {
        projectSummary
        wordCount
        creatorProfile
        purpose
      }
      sampleDesignPostMetadata {
        creatorProfile
        purpose
        image {
          sourceUrl
          mediaDetails {
            height
            width
          }
        }
      }
    }
    
    posts(where:{orderby:{field: SLUG, order:ASC} }, first: 3 ) {
      edges {
        node {
          id
          date
          title
          excerpt
          slug
          author {
            node {
              avatar {
                url
              }
              name
            }
          }
          categories {
            nodes {
              id
              slug
              name
            }
          }
          featuredImage {
            node {
              sourceUrl(size:MEDIUM_LARGE)
            }
          }
        }
      }
    }
  }`
}
